import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Long-form Thai guide: NAATI vs court/council-registered Thai translators. */
const TITLE = "NAATI กับนักแปลขึ้นทะเบียนศาล/สภาทนายความ ต่างกันอย่างไร";
const DESC =
  "เลือกผู้แปลให้ตรงกับหน่วยงานปลายทาง เทียบ NAATI สำหรับออสเตรเลีย กับนักแปลที่กรมการกงสุลและศาลไทยยอมรับ พร้อมเกณฑ์ตัดสินใจและข้อควรระวัง";
const URL = "/blog/naati-vs-court-registered-translator";
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "NAATI คืออะไร",
    answer:
      "NAATI (National Accreditation Authority for Translators and Interpreters) คือองค์กรรับรองมาตรฐานนักแปลและล่ามของประเทศออสเตรเลีย นักแปลที่ได้รับการรับรองจะมีหมายเลขผู้ปฏิบัติงานและตราประทับที่ระบุวันหมดอายุการรับรอง หน่วยงานราชการออสเตรเลียส่วนใหญ่กำหนดให้ใช้คำแปลจากผู้ได้รับการรับรองนี้",
  },
  {
    question: "คำแปล NAATI ใช้ยื่นกรมการกงสุลไทยได้หรือไม่",
    answer:
      "การรับรองนิติกรณ์ของกรมการกงสุลพิจารณาตามหลักเกณฑ์และรูปแบบคำแปลที่กรมการกงสุลกำหนด ซึ่งเป็นระบบแยกจากการรับรองของ NAATI ดังนั้นเอกสารที่จะยื่นนิติกรณ์ในไทยควรใช้คำแปลตามรูปแบบที่กรมการกงสุลรับ ส่วนคำแปล NAATI ใช้สำหรับยื่นหน่วยงานในออสเตรเลีย",
  },
  {
    question: "ยื่นวีซ่าออสเตรเลียต้องใช้นักแปลแบบไหน",
    answer:
      "สำหรับเอกสารที่ยื่นต่อหน่วยงานในออสเตรเลีย แนวทางที่ปลอดภัยที่สุดคือคำแปลจากนักแปลที่ได้รับการรับรอง NAATI ที่ยังไม่หมดอายุ พร้อมตราประทับ หมายเลขผู้ปฏิบัติงาน และคำรับรองผู้แปล หากเอกสารต้องผ่านการรับรองในไทยด้วย ควรทำสองชั้นตามที่แต่ละหน่วยงานกำหนด",
  },
  {
    question: "นักแปลขึ้นทะเบียนศาลคืออะไร",
    answer:
      "คือผู้แปลที่ศาลยอมรับให้จัดทำคำแปลหรือทำหน้าที่ล่ามในกระบวนการพิจารณาคดี เอกสารประกอบคดีที่เป็นภาษาต่างประเทศต้องมีคำแปลภาษาไทยที่ศาลรับ ซึ่งเป็นเงื่อนไขคนละชุดกับการรับรองเพื่อใช้ต่างประเทศ",
  },
  {
    question: "ถ้าใช้ผู้แปลผิดประเภทจะเกิดอะไรขึ้น",
    answer:
      "หน่วยงานปลายทางจะปฏิเสธคำแปลและต้องแปลใหม่ทั้งชุด ซึ่งมักกระทบกำหนดยื่นเอกสาร กรณีที่พบบ่อยคือใช้คำแปลทั่วไปยื่นหน่วยงานออสเตรเลีย หรือใช้คำแปลที่ไม่ตรงรูปแบบของกรมการกงสุลยื่นนิติกรณ์",
  },
  {
    question: "ต้องแปลเอกสารกี่ชุด",
    answer:
      "ขึ้นกับจำนวนหน่วยงานที่ต้องยื่นและว่าหน่วยงานใดเก็บต้นฉบับคำแปลไว้ โดยทั่วไปควรจัดทำเผื่ออย่างน้อยหนึ่งชุดสำรอง เพราะหน่วยงานหลายแห่งไม่คืนชุดที่ยื่น",
  },
];

export const Route = createFileRoute("/blog/naati-vs-court-registered-translator")({
  component: NaatiVsCourtTranslator,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("naati-vs-court-registered-translator", "th") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "naati คืออะไร, แปลเอกสาร naati, นักแปลขึ้นทะเบียนศาล, นักแปลรับรองกรมการกงสุล, แปลเอกสารยื่นวีซ่าออสเตรเลีย",
        },
      ],
      links: canonicalAndHreflang(URL),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บทความ", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("naati-vs-court-registered-translator"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Translation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const COMPARE: Array<{ topic: string; naati: string; thai: string }> = [
  {
    topic: "ผู้กำหนดมาตรฐาน",
    naati: "NAATI ประเทศออสเตรเลีย",
    thai: "หลักเกณฑ์ของกรมการกงสุล และการยอมรับของศาล/หน่วยงานไทย",
  },
  {
    topic: "หลักฐานบนคำแปล",
    naati: "ตราประทับ หมายเลขผู้ปฏิบัติงาน วันหมดอายุการรับรอง",
    thai: "คำรับรองผู้แปล ลายมือชื่อ และข้อมูลผู้แปลตามแบบที่กำหนด",
  },
  {
    topic: "ใช้กับหน่วยงานใด",
    naati: "หน่วยงานราชการและสถาบันในออสเตรเลีย รวมถึงงานตรวจคนเข้าเมือง",
    thai: "กรมการกงสุล สถานทูตในไทย ศาล และหน่วยงานราชการไทย",
  },
  {
    topic: "การรับรองต่อยอด",
    naati: "โดยทั่วไปไม่ต้องผ่านนิติกรณ์ไทย หากปลายทางรับคำแปล NAATI",
    thai: "มักต่อด้วยนิติกรณ์กรมการกงสุล และรับรองสถานทูตตามประเทศปลายทาง",
  },
  {
    topic: "อายุการรับรอง",
    naati: "การรับรองผู้แปลมีกำหนดต่ออายุ ต้องตรวจว่ายังไม่หมดอายุในวันที่ออกคำแปล",
    thai: "ไม่ผูกกับวันหมดอายุของผู้แปล แต่ผูกกับความถูกต้องของรูปแบบคำแปล",
  },
];

function NaatiVsCourtTranslator() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/" className="text-muted-foreground hover:underline">หน้าแรก</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:underline">บทความ</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span>NAATI vs นักแปลขึ้นทะเบียน</span>
      </nav>

      <h1>NAATI กับนักแปลขึ้นทะเบียนศาล/สภาทนายความ ต่างกันอย่างไร</h1>
      <BlogHero slug="naati-vs-court-registered-translator" locale="th" />
      <p className="speakable-summary lead answer-summary">
        NAATI เป็นระบบรับรองนักแปลของประเทศออสเตรเลีย ใช้เมื่อยื่นเอกสารต่อหน่วยงานในออสเตรเลีย
        ส่วนนักแปลที่กรมการกงสุลและศาลไทยยอมรับ ใช้เมื่อเอกสารต้องผ่านการรับรองในประเทศไทย
        หรือใช้ในกระบวนการพิจารณาคดี ทั้งสองระบบไม่แทนกัน การเลือกผิดประเภททำให้ต้องแปลใหม่ทั้งชุด
        หลักตัดสินใจคือ "ใครเป็นผู้รับเอกสารฉบับสุดท้าย" ไม่ใช่ "ภาษาอะไร"
      </p>

      <h2>1. ทำไมการเลือกผู้แปลจึงเป็นเรื่องของหน่วยงานปลายทาง</h2>
      <p>
        คำแปลเอกสารราชการไม่ได้ถูกตัดสินด้วยความสวยงามของภาษา แต่ถูกตัดสินด้วยการที่หน่วยงานปลายทาง
        ตรวจได้ว่า "ใครแปล" และ "ตรวจสอบย้อนกลับได้หรือไม่" ระบบรับรองแต่ละประเทศจึงออกแบบให้มีร่องรอย
        ที่เจ้าหน้าที่ตรวจได้ทันที เช่น หมายเลขผู้ปฏิบัติงานของ NAATI หรือรูปแบบคำรับรองผู้แปล
        ตามที่กรมการกงสุลกำหนด เมื่อร่องรอยนั้นไม่ตรงกับที่หน่วยงานคุ้นเคย เจ้าหน้าที่จะปฏิเสธไว้ก่อน
      </p>

      <h2>2. ตารางเปรียบเทียบ</h2>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">หัวข้อ</th>
              <th className="p-2 text-left">NAATI</th>
              <th className="p-2 text-left">นักแปลที่ไทยยอมรับ</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE.map((c) => (
              <tr key={c.topic} className="border-b align-top">
                <td className="p-2 font-medium">{c.topic}</td>
                <td className="p-2">{c.naati}</td>
                <td className="p-2">{c.thai}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm">
        แหล่งอ้างอิง:{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">NAATI</a> ·{" "}
        <a href="https://consular.mfa.go.th/th/publicservice/legalization" target="_blank" rel="noreferrer">
          กรมการกงสุล กระทรวงการต่างประเทศ
        </a>{" "}
        ·{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          สภาทนายความในพระบรมราชูปถัมภ์
        </a>
      </p>

      <h2>3. เลือกอย่างไรใน 4 สถานการณ์ที่พบบ่อย</h2>
      <ol>
        <li>
          <strong>ยื่นวีซ่าหรือขอรับรองคุณวุฒิในออสเตรเลีย</strong> ใช้คำแปล NAATI ที่ยังไม่หมดอายุ
          ตรวจว่าตราประทับปรากฏบนทุกหน้า และการสะกดชื่อตรงกับหนังสือเดินทาง
        </li>
        <li>
          <strong>เอกสารไทยไปใช้ในประเทศที่ไม่ใช่ออสเตรเลีย</strong> ใช้คำแปลตามรูปแบบที่กรมการกงสุลรับ
          แล้วยื่นนิติกรณ์ ต่อด้วยสถานทูตปลายทางถ้าจำเป็น
        </li>
        <li>
          <strong>เอกสารต่างประเทศใช้ในไทย</strong> ต้องมีคำแปลภาษาไทย และมักต้องผ่านการรับรองตามช่องทางที่หน่วยงานไทยกำหนด
          หน่วยงานที่ต่างกันเช่นสำนักงานเขต ศาล หรือกรมพัฒนาธุรกิจการค้า อาจกำหนดรูปแบบไม่เหมือนกัน
        </li>
        <li>
          <strong>เอกสารประกอบคดีในศาลไทย</strong> ต้องใช้คำแปลที่ศาลยอมรับ และหลายกรณีต้องมีล่ามในวันพิจารณา
          ควรเตรียมคำแปลให้ครบทั้งชุดเอกสารที่จะอ้างอิง ไม่ใช่เฉพาะฉบับสำคัญ
        </li>
      </ol>

      <h2>4. รายละเอียดที่มักถูกมองข้าม</h2>
      <ul>
        <li>
          <strong>ความสม่ำเสมอของชื่อ</strong> ชื่อบุคคล ชื่อสถานที่ และชื่อหน่วยงาน ต้องสะกดเหมือนกันทุกฉบับในชุดเดียวกัน
          หากเคยยื่นเอกสารกับหน่วยงานนั้นมาก่อน ให้ยึดการสะกดเดิมเพื่อไม่ให้ระบบมองว่าเป็นคนละคน
        </li>
        <li>
          <strong>ตัวเลขและวันที่</strong> ปีพุทธศักราชกับคริสต์ศักราช เป็นจุดผิดพลาดที่พบมาก
          คำแปลควรระบุให้ชัดว่าอ้างปีปฏิทินใด
        </li>
        <li>
          <strong>ตราประทับและข้อความบนตรา</strong> ต้องถอดข้อความบนตราด้วย ไม่ใช่เขียนว่า "ตราประทับ" เพียงคำเดียว
        </li>
        <li>
          <strong>หน้าที่ว่างและหน้าหลัง</strong> เอกสารบางฉบับมีข้อความด้านหลัง หากไม่แปลจะถูกถือว่าคำแปลไม่ครบ
        </li>
        <li>
          <strong>ความชัดของไฟล์ต้นฉบับ</strong> สแกนความละเอียดต่ำทำให้ผู้แปลอ่านเลขที่เอกสารผิด และนำไปสู่การตีกลับ
        </li>
      </ul>

      <h2>5. ลำดับการทำงานที่เราใช้กับเคสสองระบบ</h2>
      <p>
        บางเคสต้องใช้ทั้งสองระบบ เช่น คู่สมรสชาวออสเตรเลียที่จดทะเบียนสมรสในไทย
        แล้วต้องยื่นเอกสารทั้งกับหน่วยงานไทยและหน่วยงานออสเตรเลีย เราจะแยกสายงานตั้งแต่ต้น
        เพื่อไม่ให้คำแปลชุดหนึ่งไปทำให้อีกชุดเสียเวลา
      </p>
      <ol>
        <li>ตรวจรายการเอกสารและระบุว่าฉบับใดต้องยื่นที่ใด</li>
        <li>คัดเอกสารต้นทางใหม่ให้อยู่ในกรอบเวลาที่ปลายทางกำหนด</li>
        <li>จัดคำแปลสองสาย คือสายกรมการกงสุล และสาย NAATI ตามหน่วยงานผู้รับ</li>
        <li>ยื่นนิติกรณ์และรับรองสถานทูตเฉพาะสายที่จำเป็น</li>
        <li>ส่งมอบพร้อมบัญชีรายการเอกสาร เพื่อให้ตรวจนับได้ตอนยื่น</li>
      </ol>

      <h2>6. เราช่วยอะไรได้</h2>
      <p>
        ทีมงานของเรามีทั้งทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร และเครือข่ายนักแปลสำหรับทั้งสองระบบ
        เราให้คำปรึกษาก่อนเริ่มงานทุกเคส ตรวจเอกสารก่อนยื่น และแจ้งความเสี่ยงที่จะถูกตีกลับล่วงหน้า
        ดูรายละเอียดที่ <Link to="/services">บริการทั้งหมด</Link> ·{" "}
        <Link to="/faq">คำถามที่พบบ่อย</Link> · <Link to="/contact">ติดต่อเรา</Link> หรือโทร{" "}
        <a href="tel:+66948958999">094-895-8999</a>
      </p>

      <section className="not-prose mt-12 border-t pt-8">
        <h2 className="text-2xl font-semibold mb-4">คำถามที่พบบ่อย</h2>
        <div className="space-y-4">
          {FAQ.map((f) => (
            <details key={f.question} className="rounded-lg border p-4">
              <summary className="font-medium cursor-pointer">{f.question}</summary>
              <p className="mt-2 text-muted-foreground faq-answer">{f.answer}</p>
            </details>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          ตรวจทานเนื้อหาโดยทีมทนายผู้ทำคำรับรองลายมือชื่อและเอกสารของสำนักงาน · ตรวจทานล่าสุด 10 สิงหาคม 2569
          · เนื้อหานี้เป็นข้อมูลทั่วไปเกี่ยวกับขั้นตอน ไม่ใช่คำแนะนำทางกฎหมายเฉพาะกรณี
        </p>
      </section>
    </article>
  );
}
